<template>
  <div class="flex h-full flex-col gap-4">
    <div
      v-if="original?.item"
      class="flex shrink-0 flex-row items-center gap-3 rounded border p-1"
    >
      <h3 class="flex-1 text-base font-bold">
        {{ original.wiki_name ?? original.item.item_name }}
      </h3>
      <div class="shrink-0">
        <span
          class="rounded border px-1.5 text-base"
          :class="[
            sumSelectionItemLocations !== totalNeeded
              ? 'bg-red-400 text-white'
              : 'bg-green-400'
          ]"
        >
          {{ sumSelectionItemLocations }} / {{ totalNeeded }}
        </span>
      </div>
    </div>
    <div>
      <RadioCardComponent
        v-if="localOriginal?.item?.stockable"
        v-model="currentView"
        :options="currentViewOptions"
        hide-bullet
      />
    </div>
    <div
      v-if="original?.item"
      class="flex flex-1 flex-col gap-2 overflow-y-auto"
    >
      <template v-if="original?.item?.stockable">
        <template v-if="currentView === 'local'">
          <template
            v-for="(funeralHome, idx_fh) in stockLocalLocationDossier"
            :key="idx_fh"
          >
            <div
              v-for="(site, idx_s) in funeralHome"
              :key="idx_s"
              class="flex flex-col gap-1"
            >
              <div class="flex flex-row gap-2">
                <h3 class="font-semibold">
                  {{
                    sites.find((st) => st.id === idx_s)?.['name'] ??
                    `# ${idx_s}`
                  }}
                </h3>
                <small>
                  (
                  {{
                    funeralHomes.find((fh) => fh.id === idx_fh)?.['name'] ??
                    `# ${idx_fh}`
                  }}
                  )
                </small>
              </div>
              <div class="grid grid-cols-2 gap-2 lg:grid-cols-3">
                <template v-for="(location, idx_l) in site" :key="idx_l">
                  <StockSelectInStock
                    v-model:quantity="localOriginal.stock[idx_l].quantity"
                    :quantity-available="location"
                    :disable-add-button="localOriginal.order === 0"
                    :name="
                      locations.find((l) => l.id === idx_l)?.['name'] ??
                      `# ${idx_l}`
                    "
                  />
                </template>
              </div>
            </div>
          </template>
        </template>
        <template v-else-if="currentView === 'remote'">
          <template
            v-for="(funeralHome, idx_fh) in stockRemoteLocationDossier"
            :key="idx_fh"
          >
            <div
              v-for="(site, idx_s) in funeralHome"
              :key="idx_s"
              class="flex flex-col gap-1"
            >
              <div class="flex flex-row gap-2">
                <h3 class="font-semibold">
                  {{
                    sites.find((st) => st.id === idx_s)?.['name'] ??
                    `# ${idx_s}`
                  }}
                </h3>
                <small>
                  (
                  {{
                    funeralHomes.find((fh) => fh.id === idx_fh)?.['name'] ??
                    `# ${idx_fh}`
                  }}
                  )
                </small>
              </div>
              <div class="grid grid-cols-2 gap-2 lg:grid-cols-3">
                <template v-for="(location, idx_l) in site" :key="idx_l">
                  <StockSelectInStock
                    v-model:quantity="localOriginal.stock[idx_l].quantity"
                    :quantity-available="location"
                    :disable-add-button="localOriginal.order === 0"
                    :name="
                      locations.find((l) => l.id === idx_l)?.['name'] ??
                      `# ${idx_l}`
                    "
                  />
                </template>
              </div>
            </div>
          </template>
        </template>
        <template v-else-if="currentView === 'order'">
          <template v-if="localOriginal.item.order_mode === 'ORDERED'">
            <h3 class="capitalize-first text-lg font-bold">
              {{ $t('global.order') }}
            </h3>
            <div class="w-1/2">
              <SelectGroupedPickerComponent
                v-model="localOriginal.item.supplier_id"
                :groups="supplierList"
                name="supplier_id"
                track-by="id"
                display-key="name"
                :label="$t('attributes.supplier')"
                required
              />
            </div>
            <template v-if="localOriginal.item.supplier_id">
              <template v-if="newSupplierOrder">
                <StockSelectNewOrder
                  v-if="newSupplierOrder"
                  v-model:place-model="deliveryPlaceModel"
                  v-model:delivery-to-event-id="
                    localOriginal.new_supplier_order.delivery.event_id
                  "
                  v-model:delivery-date-time="
                    localOriginal.new_supplier_order.delivery.delivery_date_time
                  "
                  v-model:supplier-id="
                    localOriginal.new_supplier_order.recipient_id
                  "
                  v-model:new-orders="localNewOrders"
                  :supplier-with-orders="supplierWithOrders"
                  :local-original="localOriginal"
                  :events="localDossier?.events ?? []"
                  @change-view="handleQuitNewOrderView"
                  @choose-suplier-order="handleButtonSelectExistingOrder"
                />
              </template>
              <template
                v-else-if="
                  !localOriginal.item.has_supplier &&
                  localOriginal.item.supplier_id
                "
              >
                <div
                  class="mt-2 grid auto-rows-fr grid-cols-[repeat(auto-fill,_minmax(14rem,_1fr))] gap-4"
                >
                  <StockSelectSupplierOrder
                    v-for="supplier_order in supplierOrdersActive"
                    :key="supplier_order.id"
                    :supplier-order="supplier_order"
                    :selected="
                      !newSupplierOrder &&
                      localOriginal.supplier_order_id === supplier_order.id
                    "
                    @click="
                      () => handleButtonSelectExistingOrder(supplier_order.id)
                    "
                  />
                  <button
                    class="squircle-lg flex cursor-pointer items-center justify-center rounded p-3"
                    :class="[
                      newSupplierOrder
                        ? 'border-primary border'
                        : 'border-2 border-dashed'
                    ]"
                    @click="handleButtonNewOrder"
                  >
                    {{ $t('button.add_order') }}
                  </button>
                </div>
              </template>
              <div
                v-else-if="localOriginal.item.has_supplier"
                class="grid auto-rows-fr grid-cols-[repeat(auto-fill,_minmax(14rem,_1fr))] gap-4"
              >
                <StockSelectSupplierOrder
                  v-for="supplier_order in localOriginal.supplier_orders"
                  :key="supplier_order.id"
                  :supplier-order="supplier_order"
                  :selected="
                    !newSupplierOrder &&
                    localOriginal.supplier_order_id === supplier_order.id
                  "
                  @click="
                    () => handleButtonSelectExistingOrder(supplier_order.id)
                  "
                />
                <button
                  class="squircle-lg flex cursor-pointer items-center justify-center rounded p-3"
                  :class="[
                    newSupplierOrder
                      ? 'border-primary border'
                      : 'border-2 border-dashed'
                  ]"
                  @click="handleButtonNewOrder"
                >
                  {{ $t('button.add_order') }}
                </button>
              </div>
            </template>
          </template>
        </template>
      </template>
      <template v-else>
        <h3 class="capitalize-first text-lg font-bold">
          {{ $t('global.order') }}
        </h3>

        <div class="w-1/2">
          <SelectGroupedPickerComponent
            v-model="localOriginal.item.supplier_id"
            :groups="supplierList"
            name="supplier_id"
            track-by="id"
            display-key="name"
            :label="$t('attributes.supplier')"
            :disabled="localOriginal.item.has_supplier"
            required
          />
        </div>
        <div v-if="localOriginal.item.supplier_id">
          <StockSelectNewOrder
            v-if="newSupplierOrder"
            v-model:place-model="deliveryPlaceModel"
            v-model:delivery-to-event-id="
              localOriginal.new_supplier_order.delivery.event_id
            "
            v-model:delivery-date-time="
              localOriginal.new_supplier_order.delivery.delivery_date_time
            "
            v-model:supplier-id="localOriginal.new_supplier_order.recipient_id"
            v-model:new-orders="localNewOrders"
            :has-supplier="localOriginal.item.has_supplier"
            :supplier-with-orders="supplierWithOrders"
            :local-original="localOriginal"
            :events="localDossier?.events ?? []"
            @change-view="handleQuitNewOrderView"
            @choose-suplier-order="handleButtonSelectExistingOrder"
          />
          <div
            v-else-if="
              !localOriginal.item.has_supplier && localOriginal.item.supplier_id
            "
          >
            <div
              class="mt-2 grid auto-rows-fr grid-cols-[repeat(auto-fill,_minmax(14rem,_1fr))] gap-4"
            >
              <StockSelectSupplierOrder
                v-for="supplier_order in supplierOrdersActive"
                :key="supplier_order.id"
                :supplier-order="supplier_order"
                :selected="
                  !newSupplierOrder &&
                  localOriginal.supplier_order_id === supplier_order.id
                "
                @click="
                  () => handleButtonSelectExistingOrder(supplier_order.id)
                "
              />
              <button
                class="squircle-lg flex cursor-pointer items-center justify-center rounded p-3"
                :class="[
                  newSupplierOrder
                    ? 'border-primary border'
                    : 'border-2 border-dashed'
                ]"
                @click="handleButtonNewOrder"
              >
                {{ $t('button.add_order') }}
              </button>
            </div>
          </div>
          <div
            v-else-if="localOriginal.item.has_supplier"
            class="grid auto-rows-fr grid-cols-[repeat(auto-fill,_minmax(14rem,_1fr))] gap-4"
          >
            <StockSelectSupplierOrder
              v-for="supplier_order in localOriginal.supplier_orders"
              :key="supplier_order.id"
              :supplier-order="supplier_order"
              :selected="
                !newSupplierOrder &&
                localOriginal.supplier_order_id === supplier_order.id
              "
              @click="() => handleButtonSelectExistingOrder(supplier_order.id)"
            />
            <button
              v-if="original.item.has_supplier"
              class="squircle-lg flex cursor-pointer items-center justify-center rounded p-3"
              :class="[
                newSupplierOrder
                  ? 'border-primary border'
                  : 'border-2 border-dashed'
              ]"
              @click="handleButtonNewOrder"
            >
              {{ $t('button.add_order') }}
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import RadioCardComponent from '@c/BaseFormComponent/RadioCardComponent.vue'
import StockSelectSupplierOrder from '@c/Stock/Component/component/StockSelectSupplierOrder.vue'
import StockSelectInStock from '@c/Stock/Component/component/StockSelectInStock.vue'
import StockSelectNewOrder from '@c/Stock/Component/component/StockSelectNewOrder.vue'
import SelectGroupedPickerComponent from '@c/BaseFormComponent/SelectGroupedPickerComponent.vue'

export default {
  name: 'AvailableItem',
  components: {
    SelectGroupedPickerComponent,
    StockSelectNewOrder,
    StockSelectInStock,
    StockSelectSupplierOrder,
    RadioCardComponent
  },
  props: {
    original: {
      type: Object,
      required: true
    },
    newOrders: {
      type: Array,
      required: false,
      default: () => []
    },
    supplierWithOrders: {
      type: Array,
      required: false,
      default: () => []
    },
    defaultDelivery: {
      type: Object,
      required: false,
      default: () => null
    },
    page: {
      type: Number,
      required: true
    }
  },
  emits: ['update:original', 'update:new-orders'],
  data() {
    return {
      newSupplierOrder: false,
      selectedSupplierOrderId: null,
      currentView: 'order',
      activeViewByPage: {},
      internalPage: 0
    }
  },
  computed: {
    ...mapGetters({
      dossier: 'dossier/getDossier',
      invoice: 'invoicing/getInvoice',
      funeralHomes: 'metadata/getFuneralHomesMetadata',
      sites: 'metadata/getSitesMetadata',
      locations: 'metadata/getLocationsMetadata',
      suppliers: 'metadata/getSuppliersMetadata'
    }),
    supplierList() {
      const groups = {}

      for (const item of this.suppliers) {
        const key = item.type ?? '__undefined__'
        const label = item.type_label ?? this.$t('attributes.undefined') // adapte la clé i18n
        if (!groups[key]) groups[key] = { key, label, options: [] }
        groups[key].options.push(item)
      }

      const defined = Object.values(groups).filter(
        (g) => g.key !== '__undefined__'
      )
      const undefinedGroup = groups['__undefined__']
      const allGrouped = undefinedGroup ? [...defined, undefinedGroup] : defined
      return allGrouped
    },
    localDossier() {
      if (this.$route.name === 'invoice_info_route') {
        return this.invoice?.dossier
      } else {
        return this.dossier
      }
    },
    funeralHomeId() {
      if (this.dossier) {
        return this.dossier.funeral_home_id
      } else {
        return this.invoice?.funeral_home_id
      }
    },
    localOriginal: {
      get() {
        return this.original
      },
      set(value) {
        this.$emit('update:original', value)
      }
    },
    localNewOrders: {
      get() {
        return this.newOrders
      },
      set(value) {
        this.$emit('update:new-orders', value)
      }
    },
    deliveryPlaceModel: {
      get() {
        const delivery = this.localOriginal?.new_supplier_order?.delivery
        if (
          delivery?.place_model &&
          (delivery.place_model.full_name || delivery.place_model.name)
        ) {
          return delivery.place_model
        }
        return {
          place_id: delivery?.place_id ?? this.defaultDelivery?.id ?? null,
          place_type:
            delivery?.place_type ?? this.defaultDelivery?.kind ?? null,
          place_kind:
            delivery?.place_kind ?? this.defaultDelivery?.kind ?? null,
          full_name: this.defaultDelivery?.full_name ?? null
        }
      },
      set(value) {
        if (!value) return

        const placeKind = value.place_kind ?? value.kind ?? value.type ?? null
        const placeId = value.place_id ?? value.id ?? null

        this.localOriginal.new_supplier_order.delivery.place_name = [
          'SFU',
          'FUN'
        ].includes(value.type)
          ? value.full_name
          : (value.name?.[this.$i18n.locale] ?? value.name)
        this.localOriginal.new_supplier_order.delivery.place_type = placeKind
        this.localOriginal.new_supplier_order.delivery.place_kind = placeKind
        this.localOriginal.new_supplier_order.delivery.place_id = placeId
        this.localOriginal.new_supplier_order.delivery.place_model = value

        this.checkAndCreateTempOrder?.()
      }
    },
    supplierOrdersActive() {
      if (this.localOriginal.item.supplier_id) {
        const supplier_t = this.localOriginal.item.supplier_id
        return this.supplierWithOrders?.find(
          (supplier) => supplier.id === supplier_t
        )?.supplier_orders
      }

      return []
    },
    stockLocalLocationDossier() {
      const id = this.funeralHomeId
      if (this.localOriginal.item.stocks[id]) {
        return { [id]: this.localOriginal.item.stocks[id] }
      }
      return {}
    },
    stockRemoteLocationDossier() {
      return Object.fromEntries(
        Object.entries(this.localOriginal.item.stocks).filter(
          ([key]) => key !== this.funeralHomeId
        )
      )
    },
    countAvailableLocal() {
      return this.sumStocks(this.stockLocalLocationDossier)
    },
    countAvailableRemote() {
      return this.sumStocks(this.stockRemoteLocationDossier)
    },
    countSelectedLocal() {
      return Object.values(this.localOriginal.stock)
        .filter((item) => item.funeral_home_id === this.funeralHomeId)
        .map((item) => item.quantity)
        .filter((n) => parseInt(n) === n)
        .reduce((a, b) => a + b, 0)
    },
    countSelectedRemote() {
      return Object.values(this.localOriginal.stock)
        .filter((item) => item.funeral_home_id !== this.funeralHomeId)
        .map((item) => item.quantity)
        .filter((n) => parseInt(n) === n)
        .reduce((a, b) => a + b, 0)
    },
    totalNeeded() {
      if (!this.original.item.stockable)
        return this.original.item.remaining_quantity
      return (
        this.original.item.remaining_quantity +
        this.original.item.item_quantity +
        this.original.item.already_allocate
      )
    },
    sumSelectionItemLocations() {
      if (!this.localOriginal.item.stockable) return this.localOriginal.quantity
      return (
        Object.values(this.localOriginal.stock)
          .map((item) => item.quantity)
          .filter((n) => parseInt(n) === n)
          .reduce((a, b) => a + b, 0) +
        this.localOriginal.order +
        this.localOriginal.item.already_allocate
      )
    },
    currentViewOptions() {
      return [
        {
          value: 'local',
          label: `${this.funeralHomes.find((funeralHome) => funeralHome.id === this.funeralHomeId)?.name ?? this.$t('global.unknown')} - ${this.countSelectedLocal} ${this.$t('global.selected_v')}`,
          sub_label: `${this.countAvailableLocal} ${this.$t('global.available_v')}`,
          description: this.$t(
            'global.available_stock_to_funeral_home_dossier'
          ),
          icon: 'location-dot',
          disabled:
            this.countAvailableLocal === 0 ||
            (this.countSelectedLocal === 0 && this.localOriginal.order === 0)
        },
        {
          value: 'remote',
          label: `${this.$t('global.other')} - ${this.countSelectedRemote} ${this.$t('global.selected_v')}`,
          sub_label: `${this.countAvailableRemote} ${this.$t('global.available_v')}`,
          description: this.$t('global.available_stock_to_other_places'),
          icon: 'map-location',
          disabled:
            this.countAvailableRemote === 0 ||
            (this.countSelectedRemote === 0 && this.localOriginal.order === 0)
        },
        {
          value: 'order',
          label: `${this.$t('global.order')} - ${this.localOriginal.order} ${this.$t('global.to_be_order')}`,
          sub_label: this.$t('global.quantity_to_order'),
          description: ' ', // dirty little hack to get the design right without touching the global RadioCardComponent
          icon: 'basket-shopping',
          disabled: this.localOriginal.order <= 0
        }
      ].filter(
        (item) =>
          !item.hasOwnProperty('gate') ||
          (item.gate !== false && item.gate !== undefined)
      )
    }
  },
  watch: {
    'original.stock': {
      handler(newValue) {
        this.calcNumToOrder()
      },
      deep: true
    },
    'localOriginal.item.supplier_id': {
      handler(newValue) {
        if (newValue) {
          if (this.supplierOrdersActive?.length > 0) {
            if (!this.original.new_supplier_order.id) {
              this.newSupplierOrder = false
              this.currentView = 'order'
            }
          } else if (!this.original.new_supplier_order.id) {
            this.handleButtonNewOrder()
          }
        } else {
          this.newSupplierOrder = true
          // this.currentView = 'order'
        }
      }
    },
    'localOriginal.new_supplier_order.delivery.event_id': {
      handler(newValue) {
        this.checkAndCreateTempOrder()
      }
    },
    'localOriginal.new_supplier_order.delivery.delivery_date_time': {
      handler(newValue) {
        this.localOriginal.new_supplier_order.delivery.delivery_date_time =
          newValue
        this.localOriginal.new_supplier_order.delivery_date_time = newValue
      }
    },
    page: {
      handler(newValue) {
        if (
          this.original.new_supplier_order.id ||
          this.original.supplier_order_id?.startsWith('temp-')
        ) {
          this.newSupplierOrder = true
        } else {
          this.currentView = 'order'
          this.newSupplierOrder = false
          this.maybeAutoOpenNewOrderForDedicatedSupplier()
        }
      }
    }
  },
  mounted() {
    this.calcNumToOrder()
    this.maybeAutoOpenNewOrderForDedicatedSupplier()
  },
  methods: {
    generateUUID() {
      return 'xxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
        /[xy]/g,
        function (c) {
          const r = (Math.random() * 16) | 0
          const v = c === 'x' ? r : (r & 0x3) | 0x8
          return v.toString(16)
        }
      )
    },
    maybeAutoOpenNewOrderForDedicatedSupplier() {
      if (
        this.localOriginal?.item?.has_supplier &&
        !(this.localOriginal.supplier_orders?.length > 0) &&
        !this.localOriginal.new_supplier_order.id
      ) {
        this.handleButtonNewOrder()
      }
    },
    handleButtonNewOrder() {
      this.localOriginal.supplier_order_id = null
      this.localOriginal.new_supplier_order = {
        ...this.localOriginal.new_supplier_order,
        recipient_id: this.localOriginal.item.supplier_id,
        delivery: {
          event_id: null,
          place_id: this.defaultDelivery?.id ?? null,
          place_type: this.defaultDelivery?.kind ?? null,
          place_kind: this.defaultDelivery?.kind ?? null,
          delivery_date_time: this.defaultDelivery?.date_time ?? null
        },
        delivery_date_time: this.defaultDelivery?.date_time
      }
      this.newSupplierOrder = true
    },
    handleButtonSelectExistingOrder(supplier_order_id) {
      if (this.localOriginal.supplier_order_id === supplier_order_id) {
        this.localOriginal.supplier_order_id = null
        this.localOriginal.delivery_place_id = null
        return
      }
      this.localOriginal.delivery_place_id = null
      this.localOriginal.supplier_order_id = supplier_order_id
    },
    handleQuitNewOrderView() {
      this.newSupplierOrder = false
      this.localOriginal.new_supplier_order = {
        ...this.localOriginal.new_supplier_order,
        recipient_id: this.localOriginal.item.supplier_id,
        id: null,
        delivery: {
          event_id: null,
          place_id: null,
          place_type: null,
          place_kind: null,
          delivery_date_time: null
        }
      }
      this.currentView = 'order'
    },
    calcNumToOrder() {
      if (!this.localOriginal.item.stockable) {
        this.localOriginal.order = this.localOriginal.item.remaining_quantity
        return
      }

      this.localOriginal.order =
        this.totalNeeded -
        (Object.values(this.localOriginal.stock)
          .map((item) => item.quantity)
          .filter((n) => parseInt(n) === n)
          .reduce((a, b) => a + b, 0) +
          this.localOriginal.item.already_allocate)
    },
    sumStocks(obj) {
      let total = 0

      function deepSum(o) {
        for (const value of Object.values(o)) {
          if (typeof value === 'number') {
            total += value
          } else if (typeof value === 'object' && value !== null) {
            deepSum(value)
          }
        }
      }

      deepSum(obj)
      return total
    },
    checkAndCreateTempOrder() {
      const supplierId = this.localOriginal.item.supplier_id
      let placeId = null
      let placeType = null
      let placeKind = null
      let placeName = null
      let event = null
      const delivery = this.localOriginal.new_supplier_order.delivery

      if (delivery.event_id) {
        event = this.localDossier?.events.find(
          (event) => event.id === delivery.event_id
        )
        placeId = event?.place_model?.id
        placeType = event?.place_type
        placeKind = event?.place_kind
      } else {
        placeId = delivery.place_id
        placeType = delivery.place_type
        placeKind = delivery.place_kind
      }

      if (!supplierId) return

      const currentOrder = this.localOriginal.new_supplier_order
      const itemId = this.localOriginal.item.id

      if (currentOrder && currentOrder.id?.startsWith('temp-')) {
        const updatedOrder = {
          ...currentOrder,
          id: currentOrder.id,
          recipient_id: supplierId,
          delivery: {
            ...currentOrder.delivery,
            place_id: placeId,
            place_type: placeType,
            place_kind: placeKind,
            place_name: delivery.place_name,
            place_model: {
              place_id: placeId,
              place_type: placeType,
              place_kind: placeKind,
              full_name: delivery.place_name
            },
            event_id: delivery.event_id,
            delivery_date_time: event
              ? event.date_from
              : delivery.delivery_date_time
          },
          delivery_date_time: event
            ? event.date_from
            : delivery.delivery_date_time,
          delivery_place: {
            full_name: event ? event.place_model?.name : delivery.place_name
          },
          item_ids: [...new Set([...currentOrder.item_ids, itemId])]
        }

        const index = this.localNewOrders.findIndex(
          (order) => order.id === currentOrder.id
        )
        if (index !== -1) {
          this.localNewOrders.splice(index, 1, updatedOrder)
        }

        this.localOriginal.new_supplier_order = updatedOrder
        return
      }

      const tempOrderId = `temp-${this.generateUUID()}`
      const newOrder = {
        id: tempOrderId,
        recipient_id: supplierId,
        delivery: {
          place_type: placeType,
          place_kind: placeKind,
          place_id: placeId,
          event_id: delivery.event_id,
          place_name: delivery.place_name,
          place_model: {
            place_id: placeId,
            place_type: placeType,
            place_kind: placeKind,
            full_name: delivery.place_name
          },
          delivery_date_time: event
            ? event.date_from
            : delivery.delivery_date_time
        },
        delivery_place: {
          full_name: event ? event.place_model?.name : delivery.place_name
        },
        delivery_date_time: event
          ? delivery.date_from
          : delivery.delivery_date_time,
        item_ids: [itemId]
      }
      this.localNewOrders.push(newOrder)

      this.localOriginal.new_supplier_order = newOrder
      this.handleButtonSelectExistingOrder(newOrder.id)
    }
  }
}
</script>
